import { useState, useEffect, useRef } from 'react';
import { documentAPI } from '../../lib/api';
import { 
  FileImage, 
  Upload, 
  Trash, 
  Eye, 
  X, 
  IdentificationCard,
  FirstAidKit,
  Image,
  File,
  CheckCircle,
  Warning,
  CloudArrowUp,
  Calendar,
  PencilSimple,
  Download,
  DownloadSimple,
  EnvelopeSimple,
  CheckSquare,
  Square,
  Export
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const documentCategories = [
  {
    id: 'identification',
    title: 'Identification Documents',
    icon: IdentificationCard,
    types: [
      { value: 'insurance_front', label: 'Insurance Card (Front)', required: true },
      { value: 'insurance_back', label: 'Insurance Card (Back)', required: true },
      { value: 'drivers_license_front', label: "Driver's License (Front)", required: true },
      { value: 'drivers_license_back', label: "Driver's License (Back)", required: true },
    ]
  },
  {
    id: 'medical',
    title: 'Medical Images & Records',
    icon: FirstAidKit,
    types: [
      { value: 'xray', label: 'X-Ray', required: false },
      { value: 'mri', label: 'MRI', required: false },
      { value: 'lab_result', label: 'Lab Result', required: false },
      { value: 'medical_image', label: 'Other Medical Image', required: false },
      { value: 'other', label: 'Other Document', required: false },
    ]
  }
];

const getDocumentIcon = (type) => {
  if (type.includes('insurance') || type.includes('license')) {
    return IdentificationCard;
  }
  if (type === 'xray' || type === 'mri' || type === 'medical_image') {
    return Image;
  }
  return File;
};

export default function DocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [description, setDescription] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [editExpiryModalOpen, setEditExpiryModalOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [newExpiryDate, setNewExpiryDate] = useState('');
  const fileInputRef = useRef(null);
  
  // Export/Share state
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await documentAPI.getAll();
      setDocuments(response.data);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!selectedType) {
      toast.error('Please select a document type first');
      return;
    }

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload an image (JPEG, PNG, GIF, WebP) or PDF.');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size exceeds 10MB limit');
      return;
    }

    setUploading(true);

    try {
      // Convert to base64
      const base64 = await fileToBase64(file);
      
      const response = await documentAPI.upload({
        document_type: selectedType,
        file_name: file.name,
        file_data: base64.split(',')[1], // Remove data:... prefix
        mime_type: file.type,
        description: description || null,
        expiration_date: expirationDate || null
      });

      setDocuments(prev => [response.data, ...prev]);
      toast.success('Document uploaded successfully');
      setUploadModalOpen(false);
      setSelectedType('');
      setDescription('');
      setExpirationDate('');
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUpdateExpiration = async () => {
    if (!editingDoc || !newExpiryDate) return;
    
    try {
      await documentAPI.updateExpiration(editingDoc.id, newExpiryDate);
      setDocuments(prev => prev.map(d => 
        d.id === editingDoc.id ? { ...d, expiration_date: newExpiryDate } : d
      ));
      toast.success('Expiration date updated');
      setEditExpiryModalOpen(false);
      setEditingDoc(null);
      setNewExpiryDate('');
      fetchDocuments(); // Refresh to get updated is_expiring_soon status
    } catch (error) {
      toast.error('Failed to update expiration date');
    }
  };

  const openEditExpiryModal = (doc) => {
    setEditingDoc(doc);
    setNewExpiryDate(doc.expiration_date || '');
    setEditExpiryModalOpen(true);
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const handleDelete = async (docId) => {
    if (!confirm('Are you sure you want to delete this document?')) return;

    try {
      await documentAPI.delete(docId);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      toast.success('Document deleted');
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleView = async (doc) => {
    try {
      const response = await documentAPI.getById(doc.id);
      setSelectedDoc(response.data);
      setViewModalOpen(true);
    } catch (error) {
      toast.error('Failed to load document');
    }
  };

  // Export & Email Handlers
  const toggleDocSelection = (docId) => {
    setSelectedDocs(prev => 
      prev.includes(docId) 
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedDocs.length === documents.length) {
      setSelectedDocs([]);
    } else {
      setSelectedDocs(documents.map(d => d.id));
    }
  };

  const handleDownloadSingle = async (doc) => {
    try {
      toast.info('Preparing download...');
      const response = await documentAPI.exportSingle(doc.id);
      
      // Create blob and download
      const blob = new Blob([response.data], { type: doc.mime_type || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Document downloaded');
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Download error:', error);
      toast.error('Failed to download document');
    }
  };

  const handleBulkExport = async () => {
    if (selectedDocs.length === 0) {
      toast.error('Please select documents to export');
      return;
    }
    
    setExporting(true);
    try {
      toast.info(`Preparing ${selectedDocs.length} documents...`);
      const response = await documentAPI.exportBulk(selectedDocs);
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `documents_export_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`${selectedDocs.length} documents exported as ZIP`);
      setSelectMode(false);
      setSelectedDocs([]);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Bulk export error:', error);
      toast.error('Failed to export documents');
    } finally {
      setExporting(false);
    }
  };

  const handleEmailDocuments = async () => {
    if (selectedDocs.length === 0) {
      toast.error('Please select documents to email');
      return;
    }
    
    if (!recipientEmail) {
      toast.error('Please enter recipient email');
      return;
    }
    
    setSendingEmail(true);
    try {
      const response = await documentAPI.emailDocuments(
        selectedDocs,
        recipientEmail,
        recipientName,
        emailMessage
      );
      
      toast.success(`Documents sent to ${recipientEmail}`, {
        description: `Mode: ${response.data.email_mode}`
      });
      
      setEmailModalOpen(false);
      setRecipientEmail('');
      setRecipientName('');
      setEmailMessage('');
      setSelectMode(false);
      setSelectedDocs([]);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Email error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send documents');
    } finally {
      setSendingEmail(false);
    }
  };

  const getDocumentsByType = (type) => {
    return documents.filter(d => d.document_type === type);
  };

  const isTypeUploaded = (type) => {
    return documents.some(d => d.document_type === type);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="documents-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
            My Documents
          </h1>
          <p className="text-slate-500 mt-1">
            Upload your insurance card, ID, and medical images
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Select Mode Toggle */}
          {documents.length > 0 && (
            <button
              onClick={() => {
                setSelectMode(!selectMode);
                if (selectMode) setSelectedDocs([]);
              }}
              data-testid="select-mode-btn"
              className={`btn-outline flex items-center gap-2 ${selectMode ? 'bg-teal-50 border-teal-300 text-teal-700' : ''}`}
            >
              {selectMode ? <CheckSquare size={20} /> : <Square size={20} />}
              {selectMode ? 'Cancel' : 'Select'}
            </button>
          )}
          
          <button
            onClick={() => setUploadModalOpen(true)}
            data-testid="upload-document-btn"
            className="btn-teal flex items-center gap-2"
          >
            <CloudArrowUp size={20} />
            Upload Document
          </button>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectMode && (
        <div className="mb-6 p-4 bg-teal-50 border border-teal-200 rounded-xl flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={toggleSelectAll}
              className="text-sm text-teal-700 hover:text-teal-900 font-medium"
              data-testid="select-all-btn"
            >
              {selectedDocs.length === documents.length ? 'Deselect All' : 'Select All'}
            </button>
            <span className="text-sm text-teal-600">
              {selectedDocs.length} of {documents.length} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkExport}
              disabled={selectedDocs.length === 0 || exporting}
              data-testid="bulk-export-btn"
              className="btn-outline flex items-center gap-2 text-sm disabled:opacity-50"
            >
              {exporting ? (
                <>
                  <div className="spinner-sm" />
                  Exporting...
                </>
              ) : (
                <>
                  <Export size={18} />
                  Export ZIP ({selectedDocs.length})
                </>
              )}
            </button>
            <button
              onClick={() => setEmailModalOpen(true)}
              disabled={selectedDocs.length === 0}
              data-testid="email-documents-btn"
              className="btn-teal flex items-center gap-2 text-sm disabled:opacity-50"
            >
              <EnvelopeSimple size={18} />
              Email ({selectedDocs.length})
            </button>
          </div>
        </div>
      )}

      {/* Document Categories */}
      <div className="space-y-8">
        {documentCategories.map((category) => (
          <div key={category.id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <category.icon size={24} className="text-teal-600" />
                <h2 className="text-lg font-semibold text-slate-900">{category.title}</h2>
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-4">
                {category.types.map((type) => {
                  const uploadedDocs = getDocumentsByType(type.value);
                  const isUploaded = uploadedDocs.length > 0;
                  const DocIcon = getDocumentIcon(type.value);

                  return (
                    <div
                      key={type.value}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        isUploaded 
                          ? 'border-green-200 bg-green-50' 
                          : type.required 
                            ? 'border-amber-200 bg-amber-50' 
                            : 'border-slate-200 bg-slate-50'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            isUploaded ? 'bg-green-100' : type.required ? 'bg-amber-100' : 'bg-slate-100'
                          }`}>
                            <DocIcon size={20} className={
                              isUploaded ? 'text-green-600' : type.required ? 'text-amber-600' : 'text-slate-500'
                            } />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-900">{type.label}</span>
                              {type.required && !isUploaded && (
                                <span className="text-xs bg-amber-200 text-amber-700 px-2 py-0.5 rounded-full">Required</span>
                              )}
                            </div>
                            {isUploaded ? (
                              <div className="flex items-center gap-1 text-sm text-green-600 mt-1">
                                <CheckCircle size={14} weight="fill" />
                                <span>Uploaded</span>
                              </div>
                            ) : (
                              <span className="text-sm text-slate-500">Not uploaded</span>
                            )}
                          </div>
                        </div>
                        
                        {isUploaded && (
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleView(uploadedDocs[0])}
                              className="p-2 text-slate-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                              title="View"
                            >
                              <Eye size={18} />
                            </button>
                            <button
                              onClick={() => handleDelete(uploadedDocs[0].id)}
                              className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash size={18} />
                            </button>
                          </div>
                        )}
                      </div>

                      {!isUploaded && (
                        <button
                          onClick={() => {
                            setSelectedType(type.value);
                            setUploadModalOpen(true);
                          }}
                          data-testid={`upload-${type.value}-btn`}
                          className="mt-3 w-full py-2 border-2 border-dashed border-slate-300 rounded-lg text-sm text-slate-600 hover:border-teal-400 hover:text-teal-600 hover:bg-teal-50 transition-colors flex items-center justify-center gap-2"
                        >
                          <Upload size={16} />
                          Upload {type.label}
                        </button>
                      )}

                      {isUploaded && uploadedDocs[0] && (
                        <div className="mt-3 space-y-1">
                          <div className="text-xs text-slate-500">
                            {uploadedDocs[0].file_name} ({formatFileSize(uploadedDocs[0].file_size)})
                          </div>
                          {/* Expiration Status */}
                          {(type.value.includes('insurance') || type.value.includes('license')) && (
                            <div className="flex items-center justify-between">
                              {uploadedDocs[0].expiration_date ? (
                                <div className={`flex items-center gap-1 text-xs ${
                                  uploadedDocs[0].is_expired 
                                    ? 'text-red-600' 
                                    : uploadedDocs[0].is_expiring_soon 
                                      ? 'text-amber-600' 
                                      : 'text-slate-500'
                                }`}>
                                  {uploadedDocs[0].is_expired && <Warning size={12} weight="fill" />}
                                  {uploadedDocs[0].is_expiring_soon && !uploadedDocs[0].is_expired && <Warning size={12} weight="fill" />}
                                  <Calendar size={12} />
                                  Expires: {new Date(uploadedDocs[0].expiration_date).toLocaleDateString()}
                                  {uploadedDocs[0].is_expired && ' (Expired)'}
                                  {uploadedDocs[0].is_expiring_soon && !uploadedDocs[0].is_expired && ' (Soon)'}
                                </div>
                              ) : (
                                <span className="text-xs text-slate-400">No expiration set</span>
                              )}
                              <button
                                onClick={() => openEditExpiryModal(uploadedDocs[0])}
                                className="text-xs text-teal-600 hover:text-teal-700 flex items-center gap-1"
                                title="Edit expiration"
                              >
                                <PencilSimple size={12} />
                                Edit
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* All Documents List */}
      {documents.length > 0 && (
        <div className="mt-8 bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900">All Uploaded Documents</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {documents.map((doc) => {
              const DocIcon = getDocumentIcon(doc.document_type);
              const isSelected = selectedDocs.includes(doc.id);
              return (
                <div 
                  key={doc.id} 
                  className={`px-6 py-4 flex items-center justify-between hover:bg-slate-50 ${isSelected ? 'bg-teal-50' : ''}`}
                  data-testid={`doc-row-${doc.id}`}
                >
                  <div className="flex items-center gap-4">
                    {/* Selection Checkbox */}
                    {selectMode && (
                      <button
                        onClick={() => toggleDocSelection(doc.id)}
                        className="p-1"
                        data-testid={`select-doc-${doc.id}`}
                      >
                        {isSelected ? (
                          <CheckSquare size={24} className="text-teal-600" weight="fill" />
                        ) : (
                          <Square size={24} className="text-slate-400" />
                        )}
                      </button>
                    )}
                    
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                      <DocIcon size={20} className="text-slate-500" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{doc.file_name}</p>
                      <p className="text-sm text-slate-500">
                        {doc.document_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} 
                        {' '}&middot;{' '}{formatFileSize(doc.file_size)}
                        {' '}&middot;{' '}{new Date(doc.uploaded_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {/* Download Button */}
                    <button
                      onClick={() => handleDownloadSingle(doc)}
                      className="p-2 text-slate-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                      title="Download"
                      data-testid={`download-doc-${doc.id}`}
                    >
                      <DownloadSimple size={20} />
                    </button>
                    <button
                      onClick={() => handleView(doc)}
                      className="p-2 text-slate-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                      title="View"
                    >
                      <Eye size={20} />
                    </button>
                    <button
                      onClick={() => handleDelete(doc.id)}
                      className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash size={20} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {uploadModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">Upload Document</h3>
              <button
                onClick={() => {
                  setUploadModalOpen(false);
                  setSelectedType('');
                  setDescription('');
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="label-clinical">Document Type</label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="input-clinical"
                  data-testid="document-type-select"
                >
                  <option value="">Select document type...</option>
                  {documentCategories.map((cat) => (
                    <optgroup key={cat.id} label={cat.title}>
                      {cat.types.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              <div>
                <label className="label-clinical">Description (Optional)</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g., Chest X-ray from Jan 2024"
                  className="input-clinical"
                />
              </div>

              {/* Expiration Date for ID documents */}
              {(selectedType.includes('insurance') || selectedType.includes('license')) && (
                <div>
                  <label className="label-clinical flex items-center gap-2">
                    <Calendar size={14} className="text-slate-500" />
                    Expiration Date
                  </label>
                  <input
                    type="date"
                    value={expirationDate}
                    onChange={(e) => setExpirationDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="input-clinical"
                    data-testid="document-expiry-date"
                  />
                  <p className="text-xs text-slate-500 mt-1">We'll remind you 30 days before expiration</p>
                </div>
              )}

              <div>
                <label className="label-clinical">Select File</label>
                <div 
                  className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-teal-400 hover:bg-teal-50 transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <CloudArrowUp size={48} className="text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-600 mb-2">Click to select a file</p>
                  <p className="text-xs text-slate-400">JPEG, PNG, GIF, WebP, or PDF (max 10MB)</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="file-input"
                  />
                </div>
              </div>

              {uploading && (
                <div className="flex items-center justify-center gap-2 text-teal-600">
                  <div className="spinner w-5 h-5" />
                  <span>Uploading...</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* View Document Modal */}
      {viewModalOpen && selectedDoc && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">{selectedDoc.file_name}</h3>
                <p className="text-sm text-slate-500">
                  {selectedDoc.document_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </p>
              </div>
              <button
                onClick={() => {
                  setViewModalOpen(false);
                  setSelectedDoc(null);
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="p-4 overflow-auto max-h-[calc(90vh-80px)] flex items-center justify-center bg-slate-100">
              {selectedDoc.mime_type.startsWith('image/') ? (
                <img
                  src={`data:${selectedDoc.mime_type};base64,${selectedDoc.file_data}`}
                  alt={selectedDoc.file_name}
                  className="max-w-full max-h-full object-contain"
                />
              ) : selectedDoc.mime_type === 'application/pdf' ? (
                <iframe
                  src={`data:${selectedDoc.mime_type};base64,${selectedDoc.file_data}`}
                  className="w-full h-[70vh]"
                  title={selectedDoc.file_name}
                />
              ) : (
                <p className="text-slate-500">Preview not available for this file type</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Expiration Date Modal */}
      {editExpiryModalOpen && editingDoc && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">Update Expiration Date</h3>
              <button
                onClick={() => {
                  setEditExpiryModalOpen(false);
                  setEditingDoc(null);
                  setNewExpiryDate('');
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-600">
                Update expiration date for: <span className="font-medium">{editingDoc.document_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
              </p>
              
              <div>
                <label className="label-clinical flex items-center gap-2">
                  <Calendar size={14} className="text-slate-500" />
                  New Expiration Date
                </label>
                <input
                  type="date"
                  value={newExpiryDate}
                  onChange={(e) => setNewExpiryDate(e.target.value)}
                  className="input-clinical"
                  data-testid="edit-expiry-date"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setEditExpiryModalOpen(false);
                    setEditingDoc(null);
                    setNewExpiryDate('');
                  }}
                  className="btn-outline flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateExpiration}
                  disabled={!newExpiryDate}
                  className="btn-teal flex-1"
                  data-testid="save-expiry-btn"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Documents Modal */}
      {emailModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">Send Documents via Email</h3>
              <button
                onClick={() => {
                  setEmailModalOpen(false);
                  setRecipientEmail('');
                  setRecipientName('');
                  setEmailMessage('');
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
                <p className="text-sm text-teal-800">
                  <strong>{selectedDocs.length}</strong> document{selectedDocs.length !== 1 ? 's' : ''} selected
                </p>
              </div>
              
              <div>
                <label className="label-clinical">Recipient Email *</label>
                <input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="clinic@example.com"
                  className="input-clinical"
                  data-testid="recipient-email-input"
                  required
                />
              </div>
              
              <div>
                <label className="label-clinical">Recipient Name (Optional)</label>
                <input
                  type="text"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  placeholder="Dr. Smith or Clinic Name"
                  className="input-clinical"
                  data-testid="recipient-name-input"
                />
              </div>
              
              <div>
                <label className="label-clinical">Message (Optional)</label>
                <textarea
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  placeholder="Add a message to the recipient..."
                  className="input-clinical min-h-[80px] resize-none"
                  data-testid="email-message-input"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setEmailModalOpen(false);
                    setRecipientEmail('');
                    setRecipientName('');
                    setEmailMessage('');
                  }}
                  className="btn-outline flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEmailDocuments}
                  disabled={!recipientEmail || sendingEmail}
                  className="btn-teal flex-1 flex items-center justify-center gap-2"
                  data-testid="send-email-btn"
                >
                  {sendingEmail ? (
                    <>
                      <div className="spinner-sm" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <EnvelopeSimple size={18} />
                      Send Documents
                    </>
                  )}
                </button>
              </div>
              
              <p className="text-xs text-slate-500 text-center">
                Documents will be shared securely in compliance with HIPAA guidelines.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
